/**
 * The raw projects.theme_settings.leser object as the Theme panel edits it (plan 3b Rev. 2). The canvas checks and
 * resolves it (leserTheme) and reports the navi lock back; these helpers only shape the stored JSON.
 */

/** Immutable: one field of one part; null deletes it (back to the default), an emptied part goes too. */
export function set(raw, part, field, value) {
  const group = { ...((raw && raw[part]) || {}) };
  if (value === null) delete group[field];
  else group[field] = value;
  const off = { ...raw };
  if (Object.keys(group).length === 0) delete off[part];
  else off[part] = group;
  return off;
}

/** The stored form: version 1, and never an arrow kind the navi bar rules out (spec §3). */
export function saveFor(raw, locked) {
  const kind = raw && raw.arrows ? raw.arrows.kind : undefined;
  const off = locked.includes(kind) ? set(raw, 'arrows', 'kind', 'edge') : raw;
  return { version: 1, ...off };
}

/** The first visible page without a navi button yet, else the first visible page, else null. */
export function nextFreePage(buttons, pages) {
  const open = pages.filter((s) => s.isHidden !== true);
  const belegt = buttons.map((k) => (k && k.target ? k.target.pageId : undefined));
  return open.find((s) => !belegt.includes(s.pageId)) || open[0] || null;
}

const KINDS = ['edge', 'buttons', 'strip', 'none'];
const title = (s) => String((s && s.title) || '').slice(0, 24);

// Word starts only ("Steam" is no team), first match wins.
const word = (w) => new RegExp(`(?:^|[\\s\\-/&(])(?:${w})`, 'i');
const SYMBOL_WORDS = [
  [word('kontakt|contact'), 'mail'], [word('termin|kalender|event|veranstalt'), 'calendar'], [word('team|über uns|ueber uns|about|autor'), 'person'],
  [word('video|film'), 'play'], [word('download|pdf'), 'download'], [word('bilder|galerie|foto'), 'image'], [word('faq|fragen'), 'question'],
  [word('info|hinweis'), 'info'], [word('start|home|willkommen'), 'start'], [word('standort|anfahrt|karte'), 'place'],
];
const STANDARD_SYMBOLS = ['start', 'file', 'link', 'circle'];

/** An icon for a page title, only while the button still has a default icon (spec 3b Rev. 5 §5). */
export function symbolSuggestion(button, pageTitle) {
  if (button && button.symbol && !STANDARD_SYMBOLS.includes(button.symbol)) return button.symbol;
  const match = SYMBOL_WORDS.find(([re]) => re.test(String(pageTitle || '')));
  return match ? match[1] : 'file';
}

/**
 * Which panel rows change anything in the reader right now (spec 3b Rev. 3 §9). art: the effective arrow kind;
 * geraet: the device the canvas shows, whose rows alone appear (spec 3b Rev. 4 §7).
 */
export function visible(raw, { hasLogo, kind, device }) {
  const part = (t) => (raw && raw[t]) || {};
  const nav = part('nav');
  const navOn = nav.on === true;
  const buttons = Array.isArray(nav.buttons) ? nav.buttons : [];
  const phone = device !== 'computer';
  const { shows } = part('header');
  return {
    logoHeight: hasLogo === true && shows !== 'title' && shows !== 'nothing',
    arrowColor: kind !== 'none',
    arrowPhone: kind !== 'none' && phone,
    arrowComputer: kind !== 'none' && !phone,
    // The pager ignores the size; only edge arrows and floating buttons use it.
    arrowSize: kind === 'edge' || kind === 'buttons',
    arrowKinds: navOn ? ['edge', 'none'] : KINDS,
    nav: navOn,
    // Hide one only while the other is on and it is off: two stored "on" stay reachable.
    navWithoutSymbol: navOn && phone && !(nav.phoneWithoutText === true && nav.phoneWithoutSymbol !== true),
    navWithoutText: navOn && phone && !(nav.phoneWithoutSymbol === true && nav.phoneWithoutText !== true),
    navComputer: navOn && !phone,
    moreFrom: navOn && buttons.length > 3,
    cookies: false,
  };
}

/** navi.computer as the panel shows it; Rev. 3 stored true / false (spec 3b Rev. 4 §7). */
export function navComputer(nav) {
  const w = nav && nav.computer;
  if (w === false) return 'off';
  return ['top', 'off', 'floating'].includes(w) ? w : 'bottom';
}

/** Switching the navi on without buttons fills in the first 4 visible pages, so the bar shows at once (spec §6). */
export function navVorbelegen(pages) {
  return pages.filter((s) => s.isHidden !== true).slice(0, 4)
    .map((s, i) => ({ target: { pageId: s.pageId }, symbol: i === 0 ? 'start' : symbolSuggestion(null, s.title), text: title(s) }));
}

/** A new target; an empty label or the old page's title follows to the new page, a hand-written one stays (spec §6). */
export function targetSwitch(button, target, pages) {
  const alt = button && button.target ? pages.find((s) => s.pageId === button.target.pageId) : null;
  const newValue = target.pageId ? pages.find((s) => s.pageId === target.pageId) : null;
  const off = { ...button, target };
  if (newValue && (!button.text || (alt && button.text === title(alt)))) off.text = title(newValue);
  if (newValue) {
    off.symbol = symbolSuggestion(button, newValue.title);
    if (off.symbol !== button.symbol) delete off.svg;
  }
  return off;
}

/** Single colours a colour word replaces (spec 3b Rev. 5 §2, §4); a hex in pfeile.farbe is the Rev. 3 single colour. */
export const EINZELFARBEN = { header: ['background', 'text', 'active', 'symbol'], arrows: ['background', 'symbol'], nav: ['background', 'text', 'active', 'symbol'] };

/** The colour word of a part, 'eigen' while a single colour is set (spec 3b Rev. 5 §4). */
export function colorSchema(raw, part) {
  const t = (raw && raw[part]) || {};
  if (EINZELFARBEN[part].some((f) => t[f]) || /^#/.test(String(t.color || ''))) return 'custom';
  return ['light', 'brand', 'dark'].includes(t.color) ? t.color : null;
}

/** A colour word for one part: its single colours go (spec 3b Rev. 5 §4). */
export function setSchema(raw, part, schema) {
  let off = raw;
  EINZELFARBEN[part].forEach((f) => { off = set(off, part, f, null); });
  return set(off, part, 'color', schema);
}

/**
 * What is wrong with an app symbol for the home screen, '' when nothing (spec 2026-10-10 Baustein 3 §6.1).
 * Returns a key of editor.theme.symbolHint; the panel words it in the UI language.
 */
export function symbolHint({ url, width, height }) {
  if (!/^https:\/\//.test(url)) return 'https';
  if (!/\.png(?:$|\?)/i.test(url)) return 'png';
  if (width !== height) return 'square';
  if (width < 512) return 'small';
  return '';
}
